
import { lazy, Suspense } from "react";

import type { PageTemplate } from "../schema";
import { colors, semantic } from "../theme/tokens";

import { BasePage } from "./BasePage";
import { CapabilitiesOverview } from "./CapabilitiesOverview";
import { CommercialProposal } from "./CommercialProposal";
import { Contracts } from "./Contracts";
import { KPIFramework } from "./KPIFramework";
import { COCapabilities } from "./COCapabilities";
import { COCheckout } from "./COCheckout";
import { COProposal } from "./COProposal";
import { MAP } from "./MAP";
import { PartnershipForm } from "./PartnershipForm";
import { Marketing } from "./Marketing";
import { Onboarding } from "./Onboarding";
import { Specialty } from "./Specialty";
import { SupplierAssortmentAssessment } from "./SupplierAssortmentAssessment";
import { Uploads } from "./Uploads";
import { Welcome } from "./Welcome";
import { YourOpportunity } from "./YourOpportunity";

import type { PageTemplateComponent, PageTemplateProps } from "./types";

// The assortment matrix pulls in AG Grid (~1 MB); load it only when that page
// opens, so no other room downloads it.
const LazyCOAssortment = lazy(() => import("./COAssortment").then((m) => ({ default: m.COAssortment })));
const COAssortment = (props: PageTemplateProps) => (
    <Suspense fallback={null}>
        <LazyCOAssortment {...props} />
    </Suspense>
);

// Page-template registry — same exhaustiveness pattern as the widget registry.
// Compile error if a PageTemplate is missing.
const registry: Record<PageTemplate, PageTemplateComponent> = {
    welcome: Welcome,
    capabilities_overview: CapabilitiesOverview,
    // Programs is just a vertical widget stack — same UX as Capabilities — so
    // it shares BasePage. Kept as its own template (rather than reusing
    // capabilities_overview) so we can give it its own surface later if it
    // diverges (e.g. tier-based filtering, comparison view).
    programs: BasePage,
    faq: BasePage,
    // One canonical rich_text block under the H1; same vertical stack as FAQ.
    how_we_partner: BasePage,
    supplier_assortment_assessment: SupplierAssortmentAssessment,
    your_opportunity: YourOpportunity,
    commercial_proposal: CommercialProposal,
    partnership_form: PartnershipForm,
    contracts: Contracts,
    map: MAP,
    marketing: Marketing,
    onboarding: Onboarding,
    uploads: Uploads,

    // Legacy templates — present for backwards-compat with older provisioned
    // rooms that were stamped before the capabilities/programs split, the
    // KPIs → Uploads rename, and the white-label removal.
    kpi_framework: KPIFramework,
    specialty: Specialty,

    // Cold Lead pod additions — placeholders until that pod ships.
    supplier_portal_preview: PlaceholderTemplate("Supplier Portal Preview"),
    case_studies: PlaceholderTemplate("Case Studies"),

    // Content Optimization room (2026-09-23): shells until each page lands.
    co_proposal: COProposal,
    co_assortment: COAssortment,
    co_capabilities: COCapabilities,
    co_checkout: COCheckout,
};

// Public dispatcher used by the page route.
export function PageRenderer({ page, room }: PageTemplateProps) {
    const Component = registry[page.template];
    if (!Component) {
        return <UnknownTemplate template={page.template} />;
    }
    return <Component page={page} room={room} />;
}

function PlaceholderTemplate(name: string) {
    const Component = ({ page }: PageTemplateProps) => (
        <div
            style={{
                padding: 32,
                border: `1px dashed ${colors.mediumGray}`,
                borderRadius: 8,
                color: colors.mediumGray,
            }}
        >
            <h2 style={{ margin: 0, color: colors.navy }}>{page.title}</h2>
            <p>
                Page template &quot;{name}&quot; not yet implemented. {page.widgets.length} widget(s)
                attached.
            </p>
        </div>
    );
    Component.displayName = `Placeholder(${name})`;
    return Component;
}

function UnknownTemplate({ template }: { template: string }) {
    return (
        <div
            style={{
                padding: 32,
                border: `1px solid ${semantic.danger.border}`,
                borderRadius: 8,
                color: semantic.danger.text,
                background: semantic.danger.bg,
            }}
        >
            Unknown page template: {template}
        </div>
    );
}
